import { VisibilityState } from '@tanstack/react-table';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NumberParam, useQueryParams } from 'use-query-params';
import { useColumnVisibility } from '../components/_ui/data-table/useColumnVisibility';
import {
  DataTableName,
  DataTablePage,
  exportDataTableToCSV,
} from '../components/_ui/data-table/utils';
import {
  FOUR_G_STATUS,
  LICENSE_STATUS,
  durationTypeValues,
  headerMapForCSV,
  licenseTypeValues,
  mapRowsForCSV,
} from '../components/Licenses/utils';
import { NUMBER } from '../constant/Number.constant';
import { CommaArrayParam } from '../constant/UrlSearchParams.constant';
import { useFetchLicenseListQuery } from '../services/license.api';
import { SCOPE_TYPE } from '../services/utils';
import { License } from '../stores/types';
import { useCharger } from './useChargers';
import { useCompany } from './useCompany';

type ContextType = {
  licensesData: License[];
  isLicenseLoading: boolean;
  licensesDataCount: number;
  licenseFilterquery: any;
  totalSelectedFilters: number;
  handleSelectFilter: (type: string, value: Array<string>) => void;
  handleClearAllClick: () => void;
  handleLoadPage: (page: number) => void;
  columnVisibility: VisibilityState;
  handleColumnVisibilityChange: (columnId: string, visible: boolean) => void;
  handleResetColumns: () => void;
  setHandleResetColumnSizing: (handler: (() => void) | null) => void;
  setExportDataGetter: (dataGetter: () => License[]) => void;
  handleExportAsCSV: () => void;
};

const initialState: ContextType = {
  licensesData: [],
  isLicenseLoading: false,
  licensesDataCount: 0,
  licenseFilterquery: {},
  totalSelectedFilters: 0,
  handleSelectFilter: () => {},
  handleClearAllClick: () => {},
  handleLoadPage: () => {},
  columnVisibility: {},
  handleColumnVisibilityChange: () => {},
  handleResetColumns: () => {},
  setHandleResetColumnSizing: () => {},
  setExportDataGetter: () => {},
  handleExportAsCSV: () => {},
};

export const licenseContext = createContext(initialState);

export const useLicense = () => {
  return useContext(licenseContext);
};

interface IPROPS {
  children: React.ReactNode;
}

export const LicenseProvider = ({ children }: IPROPS) => {
  const { userScope } = useCompany();
  const { getChargerNameById } = useCharger();
  const { t } = useTranslation();
  const [licenseFilterquery, setLicenseFilterquery] = useQueryParams({
    status: CommaArrayParam,
    duration: CommaArrayParam,
    type: CommaArrayParam,
    includes4G: CommaArrayParam,
    chargerId: CommaArrayParam,
    searchValue: CommaArrayParam,
    page: NumberParam,
  });

  // Holds the reference to the function that returns the data to be exported
  const exportDataGetterRef = useRef<(() => License[]) | null>(null);

  const ROW_PER_PAGE = NUMBER.TWENTY;
  const LICENSE_COLUMN_VISIBILITY_STORAGE_KEY = 'licenses.columnVisibility';

  useEffect(() => {
    setLicenseFilterquery({ page: NUMBER.ONE });
  }, [setLicenseFilterquery]);

  const setExportDataGetter = useCallback((getter: () => License[]) => {
    exportDataGetterRef.current = getter;
  }, []);

  const totalSelectedFilters = useMemo(() => {
    const filtersObj = {
      status: licenseFilterquery.status ?? [],
      duration: licenseFilterquery.duration ?? [],
      type: licenseFilterquery.type ?? [],
      includes4G: licenseFilterquery.includes4G ?? [],
      chargerId: licenseFilterquery.chargerId ?? [],
      searchValue: licenseFilterquery.searchValue ?? [],
    };
    return Object.values(filtersObj).reduce((sum, arr) => sum + (arr?.length || 0), 0);
  }, [licenseFilterquery]);

  const filterParams = useMemo(() => {
    const includes4g = licenseFilterquery.includes4G?.map((item) => item === FOUR_G_STATUS.YES);
    const types = licenseFilterquery.type?.map((item) => {
      return licenseTypeValues[item?.toUpperCase() as keyof typeof licenseTypeValues];
    });
    const duration = licenseFilterquery.duration?.map((item) => {
      return durationTypeValues[item?.toUpperCase() as keyof typeof durationTypeValues];
    });

    return {
      includes4g: includes4g?.join(','),
      types: types?.join(','),
      duration: duration?.join(','),
    };
  }, [licenseFilterquery]);

  const licenseReqObj = useMemo(() => {
    return {
      // In case userScope is undefined in order to avoid the error sending the restricted scope "COMPANY"
      scope: userScope || SCOPE_TYPE.COMPANY,
      limit: 1000,
      offset: 0,
      'filter_in[status]': licenseFilterquery.status?.join(',')?.toUpperCase(),
      'filter_in[duration]': filterParams.duration,
      'filter_in[type]': filterParams.types,
      'filter_in[includes4G]': filterParams.includes4g,
      'filter_in[chargerId]': licenseFilterquery.chargerId?.join(','),
      'filter_like[searchValue]': licenseFilterquery.searchValue?.join(','),
    };
  }, [licenseFilterquery, filterParams, ROW_PER_PAGE, userScope]);

  const getBgColor = (status: string) => {
    return status.toLowerCase() === LICENSE_STATUS.EXPIRED ? 'bg-warning_light' : '';
  };

  const { licensesData, isLicenseLoading, licensesDataCount } = useFetchLicenseListQuery(
    licenseReqObj,
    {
      selectFromResult: (endPoint) => ({
        licensesData:
          endPoint.data?.entities.map((item: License) => ({
            ...item,
            bg: getBgColor(item.status),
          })) || [],
        isLicenseLoading: endPoint.isLoading || endPoint.isFetching,
        licensesDataCount: endPoint.data?.totalCount || 0,
      }),
    },
  );

  const handleSelectFilter = useCallback(
    (type: string, value: string[]) => {
      const filterVal = value?.length ? value : undefined;
      setLicenseFilterquery({ [type]: filterVal });
    },
    [setLicenseFilterquery],
  );

  const handleClearAllClick = useCallback(() => {
    setLicenseFilterquery({
      searchValue: undefined,
      status: undefined,
      duration: undefined,
      includes4G: undefined,
      type: undefined,
      chargerId: undefined,
      page: 1,
    });
  }, [setLicenseFilterquery]);

  const handleLoadPage = useCallback(
    (page: number) => {
      setLicenseFilterquery({ page: page });
    },
    [setLicenseFilterquery],
  );

  const defaultColumnVisibility = useMemo<VisibilityState>(
    () => ({
      licenseKey: true,
      status: true,
      assignedCharger: true,
      type: true,
      ports: true,
      includes4G: true,
      duration: true,
      remaining: true,
      expirationDate: true,
      orderId: true,
    }),
    [],
  );

  const {
    columnVisibility,
    handleColumnVisibilityChange,
    handleResetColumns,
    setHandleResetColumnSizing,
  } = useColumnVisibility(LICENSE_COLUMN_VISIBILITY_STORAGE_KEY, defaultColumnVisibility);

  const handleExportAsCSV = useCallback(() => {
    const visibleColumnIds = Object.entries(columnVisibility)
      .filter(([, visible]) => visible)
      .filter(([id]) => id !== 'action')
      .map(([id]) => id);

    const dataToExport = exportDataGetterRef.current ? exportDataGetterRef.current() : licensesData;

    const rows = mapRowsForCSV(dataToExport, visibleColumnIds, getChargerNameById, t);
    exportDataTableToCSV(
      rows,
      headerMapForCSV(t),
      visibleColumnIds,
      'License',
      DataTablePage.LICENSES,
      DataTableName.LICENSES,
    );
  }, [licensesData, columnVisibility, getChargerNameById, t]);

  const values = useMemo(() => {
    return {
      licensesData,
      isLicenseLoading,
      licensesDataCount,
      licenseFilterquery,
      totalSelectedFilters,
      handleSelectFilter,
      handleClearAllClick,
      handleLoadPage,
      columnVisibility,
      handleColumnVisibilityChange,
      handleResetColumns,
      setHandleResetColumnSizing,
      setExportDataGetter,
      handleExportAsCSV,
    };
  }, [
    licensesData,
    isLicenseLoading,
    licensesDataCount,
    licenseFilterquery,
    totalSelectedFilters,
    handleSelectFilter,
    handleClearAllClick,
    handleLoadPage,
    columnVisibility,
    handleColumnVisibilityChange,
    handleResetColumns,
    setHandleResetColumnSizing,
    setExportDataGetter,
    handleExportAsCSV,
  ]);

  return <licenseContext.Provider value={values}>{children}</licenseContext.Provider>;
};
